/**
 * zkLogin transaction signer.
 * Signs and executes Sui TransactionBlocks using the ephemeral keypair + ZK proof.
 * Supports Shinami gas sponsorship so users don't need SUI tokens.
 */
import { Buffer } from 'buffer';
import { TransactionBlock } from '@mysten/sui.js/transactions';
import { getZkLoginSignature, genAddressSeed } from '@mysten/zklogin';
import { suiClient } from './sui-client';
import { getEphemeralKeypair, getAuthData, refreshZkProof } from './zklogin-auth';
import { canSponsor, sponsorTransaction } from './shinami-gas';

const STORAGE_KEYS = {
  MAX_EPOCH: 'coffee_rewards_max_epoch',
} as const;

/**
 * Map Move abort codes from the loyalty_program contract to user-friendly messages.
 */
const MOVE_ABORT_MESSAGES: Record<number, string> = {
  0: 'Insufficient points for this action',
  1: 'Invalid points value',
  2: 'Unauthorized — not the shop owner',
  3: 'Account already registered at this shop',
  4: 'Too soon! Wait 5 min between stamps',
  5: 'Daily stamp limit reached — try tomorrow',
  6: 'Account not found — refresh & retry',
  10: 'Invalid quantity — must be 1-5 stamps',
};

function parseMoveAbort(errMsg: string): string {
  // Try to extract abort code from "MoveAbort(...), CODE)" pattern
  const abortMatch = errMsg.match(/MoveAbort\([^)]*\)\s*,\s*(\d+)\)/);
  if (abortMatch) {
    const code = parseInt(abortMatch[1]);
    if (MOVE_ABORT_MESSAGES[code]) return MOVE_ABORT_MESSAGES[code];
    return `On-chain error (abort code ${code})`;
  }
  // Fallback: check for function name clues
  if (errMsg.includes('record_purchase')) return 'Stamp failed — wait 5 min between stamps';
  return 'Transaction rejected by smart contract';
}

/**
 * Validate that a stored proof string contains the expected Shinami structure.
 * Shinami returns { zkProof: { proofPoints: { a, b, c }, issBase64Details, headerBase64 } }.
 */
function isValidProof(proofStr: string): boolean {
  try {
    const parsed = JSON.parse(proofStr);
    const proof = parsed.zkProof || parsed;
    return Boolean(
      proof?.proofPoints?.a?.length &&
      proof?.issBase64Details?.value &&
      proof?.headerBase64
    );
  } catch {
    return false;
  }
}

/**
 * Identify which proof field is missing so the diagnostic can name the
 * actual problem instead of always blaming proofPoints.
 */
function describeMissingProofField(proofStr: string): string | null {
  try {
    const parsed = JSON.parse(proofStr);
    const proof = parsed.zkProof || parsed;
    if (!proof?.proofPoints?.a?.length) return 'proofPoints.a';
    if (!proof?.issBase64Details?.value) return 'issBase64Details.value';
    if (!proof?.headerBase64) return 'headerBase64';
    return null;
  } catch {
    return 'proof JSON';
  }
}

/**
 * Diagnose why signing can't proceed. Returns a human-readable reason.
 */
export function getSignDiagnostic(): string {
  const keypair = getEphemeralKeypair();
  if (!keypair) return 'Ephemeral keypair missing (session expired or never created). Please log in again.';

  const authData = getAuthData();
  if (!authData.proof) return 'ZK proof missing. Please log in again.';
  if (authData.proof === 'dev_mode_no_proof') return 'Still using dev mode proof. Please log out and log in again.';

  const maxEpoch = parseInt(localStorage.getItem(STORAGE_KEYS.MAX_EPOCH) || '0');
  if (!maxEpoch) return 'Max epoch missing (session expired). Please log in again.';

  if (!isValidProof(authData.proof)) {
    const missing = describeMissingProofField(authData.proof);
    return `ZK proof is malformed (empty ${missing}). Click "Reset session" to fix.`;
  }

  return 'All signing prerequisites met.';
}

/**
 * Check if we can actually sign transactions (valid keypair + real ZK proof).
 */
export function canSign(): boolean {
  const keypair = getEphemeralKeypair();
  if (!keypair) return false;

  const authData = getAuthData();
  if (!authData.proof || authData.proof === 'dev_mode_no_proof') return false;

  const maxEpoch = parseInt(localStorage.getItem(STORAGE_KEYS.MAX_EPOCH) || '0');
  if (!maxEpoch) return false;

  if (!isValidProof(authData.proof)) return false;

  return true;
}

/**
 * If everything except the proof is healthy, try a one-shot proof refresh
 * before giving up. Heals transient prover hiccups without a relogin.
 */
async function tryAutoRecoverProof(): Promise<boolean> {
  const keypair = getEphemeralKeypair();
  const authData = getAuthData();
  const maxEpoch = parseInt(localStorage.getItem(STORAGE_KEYS.MAX_EPOCH) || '0');
  if (!keypair || !authData.jwt || !authData.salt || !maxEpoch) return false;
  if (authData.proof && isValidProof(authData.proof)) return false;

  try {
    console.warn('[zklogin-signer] Proof invalid — attempting auto-recovery via fresh prover call');
    await refreshZkProof();
    const ok = canSign();
    console.log('[zklogin-signer] Auto-recovery', ok ? 'succeeded' : 'failed');
    return ok;
  } catch (err: any) {
    console.error('[zklogin-signer] Auto-recovery threw:', err?.message || err);
    return false;
  }
}

/**
 * Sign a TransactionBlock with zkLogin credentials and execute it on-chain.
 * If Shinami Gas Station is configured, transactions are gas-sponsored.
 * Falls back to user-pays-gas if sponsorship fails.
 */
export async function signAndExecuteTransaction(tx: TransactionBlock) {
  if (!canSign()) {
    const recovered = await tryAutoRecoverProof();
    if (!recovered) {
      const diagnostic = getSignDiagnostic();
      console.error('[zklogin-signer] canSign() = false:', diagnostic);
      throw new Error(diagnostic);
    }
  }

  const keypair = getEphemeralKeypair()!;
  const authData = getAuthData();
  // Shinami returns { zkProof: { proofPoints, issBase64Details, headerBase64 } }.
  // Unwrap the nested key so we get the flat proof fields.
  const proofData = JSON.parse(authData.proof!);
  const zkProof = proofData.zkProof || proofData;

  // Validate proof has expected structure before passing to getZkLoginSignature
  if (!zkProof?.proofPoints?.a || !zkProof?.issBase64Details || !zkProof?.headerBase64) {
    console.error('[zklogin-signer] Malformed ZK proof:', JSON.stringify(zkProof).slice(0, 200));
    throw new Error('ZK proof is malformed (missing proofPoints). Please log out and log in again.');
  }

  const maxEpoch = parseInt(localStorage.getItem(STORAGE_KEYS.MAX_EPOCH) || '0');

  let txBytesBase64: string;
  let userSignature: string;
  let sponsorSignature: string | null = null;

  if (canSponsor()) {
    // === Try gas-sponsored flow first (Shinami pays gas) ===
    try {
      console.log('[zklogin-signer] Attempting Shinami gas sponsorship...');

      // Build TransactionKind only (no gas info — Shinami adds it)
      tx.setSenderIfNotSet(authData.address!);
      const txKindBytes = await tx.build({ client: suiClient, onlyTransactionKind: true });
      const txKindBase64 = Buffer.from(txKindBytes).toString('base64');

      // Get sponsored transaction from Shinami
      const sponsorResult = await sponsorTransaction(txKindBase64, authData.address!);
      sponsorSignature = sponsorResult.signature;
      txBytesBase64 = sponsorResult.txBytes;

      // Sign the sponsored TransactionData bytes with ephemeral keypair
      const sponsoredTxBytes = Buffer.from(sponsorResult.txBytes, 'base64');
      const signResult = await keypair.signTransactionBlock(sponsoredTxBytes);
      userSignature = signResult.signature;

      console.log('[zklogin-signer] Gas sponsorship succeeded');
    } catch (sponsorErr: any) {
      const errMsg = sponsorErr?.message || String(sponsorErr);
      console.warn('[zklogin-signer] Gas sponsorship failed:', errMsg);

      // If the error is a Move abort (transaction would fail on-chain regardless of gas),
      // surface the real reason instead of falling back to user-pays-gas (which also fails
      // because zkLogin addresses have no SUI and masks the real error).
      if (errMsg.includes('MoveAbort') || errMsg.includes('E_PURCHASE_TOO_SOON') || errMsg.includes('Invalid params')) {
        // Parse well-known Move abort codes for user-friendly messages
        const friendlyMsg = parseMoveAbort(errMsg);
        throw new Error(friendlyMsg);
      }

      // For non-Move errors (network issues, etc.), try user-pays-gas fallback
      console.log('[zklogin-signer] Falling back to user-pays-gas...');
      sponsorSignature = null;
      try {
        const signResult = await tx.sign({ signer: keypair, client: suiClient });
        txBytesBase64 = signResult.bytes;
        userSignature = signResult.signature;
        console.log('[zklogin-signer] User-pays-gas signing succeeded');
      } catch (fallbackErr: any) {
        // If fallback also fails, throw the ORIGINAL sponsor error (more informative)
        console.error('[zklogin-signer] User-pays-gas also failed:', fallbackErr?.message);
        throw new Error(`Gas sponsorship failed: ${errMsg}`);
      }
    }
  } else {
    // === Standard flow (user pays gas, no Shinami configured) ===
    console.log('[zklogin-signer] No gas sponsorship configured — user pays gas');
    const signResult = await tx.sign({ signer: keypair, client: suiClient });
    txBytesBase64 = signResult.bytes;
    userSignature = signResult.signature;
  }

  // Compute addressSeed — required by getZkLoginSignature but NOT returned by the prover.
  // It's derived from the user's JWT claims (sub, aud) and their salt.
  const jwt = authData.jwt!;
  const jwtParts = jwt.split('.');
  const jwtPayload = JSON.parse(Buffer.from(jwtParts[1], 'base64').toString());
  const aud = Array.isArray(jwtPayload.aud) ? jwtPayload.aud[0] : jwtPayload.aud;
  const addressSeed = genAddressSeed(
    BigInt(authData.salt!),
    'sub',
    jwtPayload.sub,
    aud,
  ).toString();

  // Combine ephemeral signature with ZK proof to create zkLogin signature
  const zkLoginSig = getZkLoginSignature({
    inputs: {
      ...zkProof,
      addressSeed,
    },
    maxEpoch,
    userSignature,
  });

  // Execute on-chain (with both sigs for sponsored, single sig otherwise)
  const signatures = sponsorSignature ? [zkLoginSig, sponsorSignature] : zkLoginSig;

  const result = await suiClient.executeTransactionBlock({
    transactionBlock: txBytesBase64,
    signature: signatures,
    options: {
      showEffects: true,
      showEvents: true,
    },
  });

  // Check for on-chain execution failure
  const status = (result as any)?.effects?.status;
  if (status?.status === 'failure') {
    console.error('[zklogin-signer] Transaction executed but FAILED on-chain:', status.error);
    throw new Error(`On-chain error: ${status.error}`);
  }

  console.log('[zklogin-signer] Transaction SUCCESS:', (result as any)?.digest);
  return result;
}
